/* eslint-disable @typescript-eslint/no-explicit-any */

"use client";

import React, { FocusEvent, useCallback, useEffect, useRef, useState } from "react";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { ArrowRight, Locate, X } from "lucide-react";

import GoogleMapComponent from "./GoogleMapComponent";
import { IBranch } from "@/app/[lang]/locations/helpers";
import ContentLayout from "../layouts/ContentLayout";
import AppText from "../other/AppText";
import { cn } from "@/lib/utils";
import { AppIconButton } from "../buttons/AppIconButton";
import BranchDirectory from "./BranchDirectory";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import NearestLocations from "./NearestLocations";

const LocationsIndex = ({
  branches,
  center,
  zoom,
  dict,
}: {
  branches: IBranch[];
  zoom: number;
  center: { lat: number; lng: number };
  dict: { [key: string]: string };
}) => {
  const [viewSelected, setViewSelected] = useState("search");
  const [mapZoom, setMapZoom] = useState(zoom);
  const [mapCenter, setMapCenter] = useState(center);

  const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken>();
  const [autocompleteService, setAutocompleteService] = useState<google.maps.places.AutocompleteService | null>(null);
  const [placesService, setPlacesService] = useState<google.maps.places.PlacesService | null>(null);
  const [predictionResults, setPredictionResults] = useState<Array<google.maps.places.AutocompletePrediction>>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [inputFocus, setInputFocus] = useState(false);

  const [nearestMarkers, setNearestMarkers] = useState({});

  const [placeAutocomplete, setPlaceAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<google.maps.places.PlaceResult | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const firstPrediction = useRef<string | null>(null);

  const map = useMap();
  const places = useMapsLibrary("places");

  useEffect(() => {
    document.addEventListener("click", clickListener);

    return () => document.removeEventListener("click", clickListener);
  }, []);

  const clickListener = (e: any) => {
    const element = e.target as HTMLElement;

    if (element.tagName === "INPUT") return;

    const cmdGroup = element.getAttribute("cmdk-group");
    const cmdItem = element.getAttribute("cmdk-item");

    if (cmdGroup || cmdItem) return;

    setInputFocus(false);
  };

  useEffect(() => {
    if (predictionResults && predictionResults[0]) {
      firstPrediction.current = predictionResults[0].place_id;
    }
  }, [predictionResults]);

  useEffect(() => {
    if (!places || !map) return;

    setAutocompleteService(new places.AutocompleteService());
    setPlacesService(new places.PlacesService(map));
    setSessionToken(new places.AutocompleteSessionToken());

    return () => setAutocompleteService(null);
  }, [map, places]);

  useEffect(() => {
    const lat = selectedPlace?.geometry?.location?.lat();
    const lng = selectedPlace?.geometry?.location?.lng();

    if (lat && lng) {
      setMapCenter({ lat, lng });
      map?.setZoom(10);
      // if (map && selectedPlace?.geometry?.viewport) {
      //   // map.fitBounds(selectedPlace.geometry.viewport, { top: 30, right: 10, left: 50 });
      //   map.fitBounds(selectedPlace.geometry.viewport);
      //   map.setZoom(map ? map.getZoom() - 3 : 7);
      // }
    }
  }, [map, selectedPlace]);

  // places initialized
  useEffect(() => {
    if (!places || !inputRef.current) return;

    const options = {
      fields: ["geometry", "name", "formatted_address"],
    };

    setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
  }, [places]);

  // place-autocomplete change listener
  useEffect(() => {
    if (!placeAutocomplete) return;

    placeAutocomplete.addListener("place_changed", () => {
      setSelectedPlace(placeAutocomplete.getPlace());
    });
  }, [placeAutocomplete]);

  const fetchPredictions: any = useCallback(
    async (inputValue: string) => {
      if (!autocompleteService || !inputValue) {
        setPredictionResults([]);
        return;
      }

      const request = {
        input: inputValue,
        sessionToken,
        types: ["(regions)"],
        componentRestrictions: { country: ["us", "ca"] },
      };
      const response = await autocompleteService.getPlacePredictions(request);

      setPredictionResults(response.predictions);
    },
    [autocompleteService, sessionToken]
  );

  const onInputChange = useCallback(
    (value: string) => {
      setInputFocus(true);
      setInputValue(value);
      fetchPredictions(value);
    },
    [fetchPredictions]
  );

  // click on "use my location" button
  const handleMyLocation = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setMapCenter({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });

          setMapZoom(12);
          handleClearInput();
        },
        (error) => {
          if (error.code == error.PERMISSION_DENIED) {
            console.log("[GEOLOCATION ERROR]: Blocked by user");
          }
        }
      );
    } else {
      console.log("Geolocation is not available in your browser.");
    }
  };

  // click on prediction in autocomplete list
  const handlePredictionSelect = (value: string) => {
    const selected = predictionResults.find((pred) => pred.description.toLowerCase() === value);

    if (selected) {
      firstPrediction.current = selected.place_id;
      fetchQueryCoordinates(firstPrediction.current);
    }
  };

  // key down on places input
  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (!predictionResults.length && inputValue && firstPrediction.current) {
        prepareQuery();
      }
    } else if (e.key === "Escape") {
      handleClearInput();
    }
  };

  // click on arrow on places input
  const handleArrowClick = () => prepareQuery();

  const handleClearInput = () => {
    firstPrediction.current = null;
    onInputChange("");
  };

  // get place_id from ref or from first prediction result
  const prepareQuery = () => {
    if (map) {
      if (firstPrediction.current) {
        fetchQueryCoordinates(firstPrediction.current);
      } else if (predictionResults[0]) {
        firstPrediction.current = predictionResults[0].place_id;
        fetchQueryCoordinates(firstPrediction.current);
      } else {
        console.log("[MAPS]: predictionResults or firstPrediction empty");
      }
    } else {
      console.log("[API ERROR]: map or geo doesn't exists");
    }
  };

  const fetchQueryCoordinates = (placeId: string) => {
    if (places) {
      const detailRequestOptions = {
        placeId,
        fields: ["geometry", "name", "formatted_address"],
        sessionToken,
      };

      const detailsRequestCallback = (placeDetails: google.maps.places.PlaceResult | null) => {
        setSelectedPlace(placeDetails);
        setPredictionResults([]);
        setInputValue(placeDetails?.formatted_address ?? "");
        setSessionToken(new places.AutocompleteSessionToken());
      };

      placesService?.getDetails(detailRequestOptions, detailsRequestCallback);
    }
  };

  const handleFocus = useCallback((e: FocusEvent<HTMLInputElement>) => {
    const element = e.target as HTMLElement;

    if (element.tagName === "BUTTON") return;
    setInputFocus(true);
  }, []);

  return (
    <ContentLayout bgColorClass="bg-black-5">
      <AppText type="SUB_HEADING_LARGE" className="text-center max-w-[734px] mx-auto mb-14">
        {dict.enterYourZipCode}
      </AppText>

      <div className="xs:flex-row mb-14 flex flex-col items-center justify-center w-full gap-8">
        <button
          id="button-location-index-view-selected-search"
          onClick={() => setViewSelected("search")}
          className={cn(
            `h-11 relative transition-all w-fit outline-royal-blue outline-offset-2
            text-black-60 font-saira text-18 font-[500] leading-[1.54] tracking-[-0.1px]`,
            viewSelected === "search" &&
              `text-royal-blue after:absolute after:h-[2px] after:bottom-0 after:left-0 after:right-0 
              after:bg-royal-blue text-ellipsis`
          )}
        >
          {dict.locationSearch}
        </button>
        <button
          id="button-location-index-view-selected-directory"
          onClick={() => setViewSelected("directory")}
          className={cn(
            `h-11 relative transition-all w-fit outline-royal-blue outline-offset-2
            text-black-60 font-saira text-18 font-[500] leading-[1.54] tracking-[-0.1px]`,
            viewSelected === "directory" &&
              `text-royal-blue after:absolute after:h-[2px] after:bottom-0 after:left-0 after:right-0 
            after:bg-royal-blue text-ellipsis`
          )}
        >
          {dict.branchDirectory}
        </button>
      </div>

      <div className={cn("hidden animate-in fade-in duration-300", viewSelected === "search" && "block")}>
        <div className="pb-[56px] max-w-[734px] mx-auto flex flex-col lg:flex-row gap-8 relative z-10 lg:items-center">
          <div className="border-b-[1px] border-black-60 w-full relative z-10 h-10">
            <Command
              label={dict.enterZipStateCity}
              className="bg-black-5 relative overflow-visible"
              onFocus={handleFocus}
            >
              <CommandInput
                value={inputValue}
                onValueChange={onInputChange}
                onKeyDown={handleKeyDown}
                placeholder={dict.enterZipStateCity}
                className={`text-ellipsis left-10 right-[70px] w-unset h-unset absolute top-0 h-8 overflow-hidden
                font-saira text-18 font-[500] leading-[1.54] tracking-[-0.1px]`}
              />

              <CommandList
                className={cn(
                  "top-11 absolute left-0 w-full hidden",
                  predictionResults.length && inputFocus && "block"
                )}
              >
                <CommandGroup className="px-4 py-3 bg-white">
                  {predictionResults.map(
                    ({ place_id, description, structured_formatting: { main_text, secondary_text } }) => (
                      <CommandItem
                        data-place-id={place_id}
                        key={place_id}
                        value={description}
                        onSelect={handlePredictionSelect}
                        className="whitespace-nowrap text-ellipsis block overflow-hidden"
                      >
                        <strong>{main_text},</strong> &nbsp;<span>{secondary_text}</span>
                      </CommandItem>
                    )
                  )}
                </CommandGroup>
              </CommandList>
            </Command>

            <button
              id="button-location-index-clear"
              title="Clear"
              className={cn(
                "top-0 right-8 absolute outline-none rounded focus-visible:outline-dijon",
                !inputValue && "hidden"
              )}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={handleClearInput}
            >
              <X size={20} aria-label="x icon" />
            </button>
            <button
              id="button-location-index-search"
              title="Search"
              className={cn(
                "absolute top-0 right-0 outline-none rounded focus-visible:outline-dijon",
                !inputValue && "hidden"
              )}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={handleArrowClick}
            >
              <ArrowRight size={20} aria-label="arrow right icon" />
            </button>
          </div>

          <div className="mt-[7px]">
            <AppIconButton
              icon={<Locate size={20} aria-label="target icon" />}
              intent="ghost"
              label={dict.useMyLocation}
              onClick={handleMyLocation}
            />
          </div>
        </div>

        {mapZoom && mapCenter && (
          <div className="w-full h-[669px] lg:h-[542px] xl:h-[632px]">
            <GoogleMapComponent
              branches={branches}
              center={mapCenter}
              zoom={mapZoom}
              setMapZoom={setMapZoom}
              setMapCenter={setMapCenter}
              moreDetails={dict.moreDetails}
              nearestMarkers={nearestMarkers}
              multiMarkers
            />
          </div>
        )}
        <NearestLocations
          selectedPlace={selectedPlace}
          moreDetails={dict.moreDetails}
          setNearestMarkers={setNearestMarkers}
        />
      </div>

      <div className={cn("hidden animate-in fade-in duration-300", viewSelected === "directory" && "block")}>
        <BranchDirectory branches={branches} dict={dict} />
      </div>
    </ContentLayout>
  );
};

export default LocationsIndex;
